import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CancelIcon from '@mui/icons-material/Cancel';
import UndoIcon from '@mui/icons-material/Undo';
import PaidIcon from '@mui/icons-material/Paid';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import { listBookings, updateBooking, listInterns, issueRefund, getBookingHistory } from '../services/adminApi';
import { formatDate } from '../utils/formatters';
import StatusChip from '../components/common/StatusChip';
import ConfirmDialog from '../components/common/ConfirmDialog';
import ScrollableChipRow from '../components/common/ScrollableChipRow';

const FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Confirmed', value: 'confirmed' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Awaiting Mentor Review', value: 'pending_report' },
  { label: 'Revision Requested', value: 'needs_revision' },
  { label: 'Completed', value: 'completed' },
  { label: 'Cancelled', value: 'cancelled' },
  { label: 'Refunded', value: 'refunded' },
  { label: 'Needs Attention', value: 'needs_admin_attention' },
];

export default function BookingsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [revertOpen, setRevertOpen] = useState(false);
  const [revertReason, setRevertReason] = useState('');
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refundAmountDollars, setRefundAmountDollars] = useState('');
  const [refundFullAmount, setRefundFullAmount] = useState(true);
  const [refundResult, setRefundResult] = useState(null);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleDateTime, setRescheduleDateTime] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleResult, setRescheduleResult] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [availableInterns, setAvailableInterns] = useState([]);
  const [selectedInternId, setSelectedInternId] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchBookings = useCallback(() => {
    setLoading(true);
    const filters = filter !== 'all' ? { status: filter } : {};
    listBookings(filters)
      .then((data) => setBookings(data.items || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleRowClick = (params) => {
    setSelectedBooking(params.row);
    setDrawerOpen(true);
  };

  const handleAssignOpen = () => {
    listInterns({ status: 'approved' })
      .then((data) => setAvailableInterns(data.items || []))
      .catch(() => setAvailableInterns([]));
    setAssignOpen(true);
  };

  const handleAssignIntern = async () => {
    if (!selectedBooking || !selectedInternId) return;
    setActionLoading(true);
    try {
      await updateBooking(selectedBooking.id || selectedBooking.booking_id, 'assign_intern', {
        intern_id: selectedInternId,
      });
      setAssignOpen(false);
      setSelectedInternId('');
      setDrawerOpen(false);
      fetchBookings();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!selectedBooking) return;
    setActionLoading(true);
    try {
      await updateBooking(selectedBooking.id || selectedBooking.booking_id, 'cancel', {});
      setCancelOpen(false);
      setDrawerOpen(false);
      fetchBookings();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevertBooking = async () => {
    if (!selectedBooking || !revertReason.trim()) return;
    setActionLoading(true);
    try {
      await updateBooking(selectedBooking.id || selectedBooking.booking_id, 'revert_to_confirmed', {
        reason: revertReason.trim(),
      });
      setRevertOpen(false);
      setRevertReason('');
      setDrawerOpen(false);
      fetchBookings();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleIssueRefund = async () => {
    if (!selectedBooking || !refundReason.trim()) return;
    let amountCents = null;
    if (!refundFullAmount) {
      const dollars = parseFloat(refundAmountDollars);
      if (!Number.isFinite(dollars) || dollars <= 0) {
        setError('Refund amount must be a positive number');
        return;
      }
      amountCents = Math.round(dollars * 100);
    }
    setActionLoading(true);
    setRefundResult(null);
    try {
      const result = await issueRefund({
        bookingId: selectedBooking.id || selectedBooking.booking_id,
        amountCents,
        reason: refundReason.trim(),
      });
      setRefundResult({ ok: true, ...result });
      // Refresh data; close after a beat so admin can see confirmation
      fetchBookings();
    } catch (err) {
      const detail = err?.response?.data?.error || err.message;
      setRefundResult({ ok: false, error: detail });
    } finally {
      setActionLoading(false);
    }
  };

  const closeRefundDialog = () => {
    setRefundOpen(false);
    setRefundReason('');
    setRefundAmountDollars('');
    setRefundFullAmount(true);
    setRefundResult(null);
    if (refundResult?.ok) {
      setDrawerOpen(false);
    }
  };

  const openRescheduleDialog = () => {
    // Default the date input to the current time_slot if present
    const current = selectedBooking?.time_slot;
    if (current) {
      // datetime-local needs YYYY-MM-DDTHH:mm (no zone suffix)
      try {
        const d = new Date(current);
        const pad = (n) => String(n).padStart(2, '0');
        const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        setRescheduleDateTime(local);
      } catch {
        setRescheduleDateTime('');
      }
    } else {
      setRescheduleDateTime('');
    }
    setRescheduleOpen(true);
  };

  const handleReschedule = async () => {
    if (!selectedBooking || !rescheduleDateTime || !rescheduleReason.trim()) return;
    setActionLoading(true);
    setRescheduleResult(null);
    try {
      // Convert the datetime-local value (local time, no zone) to ISO 8601 UTC
      const localDate = new Date(rescheduleDateTime);
      if (isNaN(localDate.getTime())) {
        setRescheduleResult({ ok: false, error: 'Invalid date/time' });
        setActionLoading(false);
        return;
      }
      const newTimeIso = localDate.toISOString();
      const result = await updateBooking(
        selectedBooking.id || selectedBooking.booking_id,
        'reschedule',
        { new_time_slot: newTimeIso, reason: rescheduleReason.trim() }
      );
      setRescheduleResult({ ok: true, ...result });
      fetchBookings();
    } catch (err) {
      const detail = err?.response?.data?.error || err.message;
      setRescheduleResult({ ok: false, error: detail });
    } finally {
      setActionLoading(false);
    }
  };

  const closeRescheduleDialog = () => {
    const wasOk = rescheduleResult?.ok;
    setRescheduleOpen(false);
    setRescheduleDateTime('');
    setRescheduleReason('');
    setRescheduleResult(null);
    if (wasOk) setDrawerOpen(false);
  };

  const openHistory = async () => {
    if (!selectedBooking) return;
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryError('');
    setHistoryEvents([]);
    try {
      const result = await getBookingHistory(selectedBooking.id || selectedBooking.booking_id);
      setHistoryEvents(result.events || []);
    } catch (err) {
      setHistoryError(err?.response?.data?.error || err.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  const columns = [
    {
      field: 'booking_id',
      headerName: 'Booking ID',
      width: 140,
      valueGetter: (params) => params.row.booking_id || params.row.id || '-',
    },
    { field: 'client_name', headerName: 'Client', flex: 1, minWidth: 150, valueGetter: (params) => params.row.client_name || params.row.client_email || '-' },
    { field: 'intern_name', headerName: 'Intern', flex: 1, minWidth: 150, valueGetter: (params) => params.row.intern_name || params.row.intern_email || 'Unassigned' },
    { field: 'time_slot', headerName: 'Time Slot', width: 170, valueFormatter: (params) => formatDate(params.value) },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => <StatusChip status={params.value} type="booking" />,
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 170,
      valueFormatter: (params) => formatDate(params.value),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Bookings
      </Typography>

      <ScrollableChipRow>
        {FILTER_OPTIONS.map((opt) => (
          <Chip
            key={opt.value}
            label={opt.label}
            onClick={() => setFilter(opt.value)}
            color={filter === opt.value ? 'primary' : 'default'}
            variant={filter === opt.value ? 'filled' : 'outlined'}
          />
        ))}
      </ScrollableChipRow>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {isMobile ? (
        <Stack spacing={1.5}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!loading && bookings.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No bookings in this view
              </Typography>
            </Paper>
          )}
          {!loading && bookings.map((b) => (
            <Paper
              key={b.id || b.booking_id || Math.random()}
              sx={{ p: 2, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
              onClick={() => handleRowClick({ row: b })}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: 'monospace' }} noWrap>
                    {(b.booking_id || b.id || '').slice(0, 8)}…
                  </Typography>
                  <Typography variant="body2" noWrap>
                    {b.client_name || b.client_email || '—'}
                  </Typography>
                </Box>
                <StatusChip status={b.status} type="booking" />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Intern: {b.intern_name || b.intern_email || 'Unassigned'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Time: {formatDate(b.time_slot)}
              </Typography>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Box sx={{ height: 600, backgroundColor: 'background.paper', borderRadius: 1 }}>
          <DataGrid
            rows={bookings}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id || row.booking_id || Math.random()}
            onRowClick={handleRowClick}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            disableRowSelectionOnClick
          />
        </Box>
      )}

      {/* Detail Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', sm: 420 } } }}
      >
        {selectedBooking && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Booking Details</Typography>
              <IconButton onClick={() => setDrawerOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              <DetailRow label="Booking ID" value={selectedBooking.booking_id || selectedBooking.id} />
              <DetailRow label="Client" value={selectedBooking.client_name || selectedBooking.client_email} />
              <DetailRow label="Client Email" value={selectedBooking.client_email} />
              <DetailRow label="Intern" value={selectedBooking.intern_name || selectedBooking.intern_email || 'Unassigned'} />
              <DetailRow label="Time Slot" value={formatDate(selectedBooking.time_slot)} />
              <DetailRow label="Service Type" value={selectedBooking.service_type} />
              <DetailRow label="Address" value={selectedBooking.address} />
              <Box>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <StatusChip status={selectedBooking.status} type="booking" />
                </Box>
              </Box>
              <DetailRow label="Created" value={formatDate(selectedBooking.created_at)} />
              {selectedBooking.notes && <DetailRow label="Notes" value={selectedBooking.notes} />}
            </Stack>

            <Divider sx={{ my: 3 }} />

            <Stack spacing={2}>
              <Button
                variant="text"
                size="small"
                startIcon={<HistoryIcon />}
                onClick={openHistory}
                sx={{ alignSelf: 'flex-start' }}
              >
                View History
              </Button>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PersonAddIcon />}
                  onClick={handleAssignOpen}
                  fullWidth
                >
                  Assign Intern
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={() => setCancelOpen(true)}
                  fullWidth
                >
                  Cancel
                </Button>
              </Stack>
              {/* Reschedule is only meaningful for confirmed bookings -- backend rejects others. */}
              {selectedBooking.status === 'confirmed' && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<ScheduleIcon />}
                  onClick={openRescheduleDialog}
                  fullWidth
                >
                  Reschedule
                </Button>
              )}
              {(selectedBooking.status === 'in_progress' || selectedBooking.status === 'pending_report') && (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<UndoIcon />}
                  onClick={() => setRevertOpen(true)}
                  fullWidth
                >
                  Revert to Confirmed
                </Button>
              )}
              {/* Refund visible on any non-already-refunded booking. Backend
                  will reject (404) if there's no succeeded payment to refund. */}
              {selectedBooking.status !== 'refunded' && selectedBooking.status !== 'partially_refunded' && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<PaidIcon />}
                  onClick={() => setRefundOpen(true)}
                  fullWidth
                >
                  Issue Refund
                </Button>
              )}
              {(selectedBooking.status === 'refunded' || selectedBooking.status === 'partially_refunded') && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Refunded {selectedBooking.refund_amount_cents ? `$${(selectedBooking.refund_amount_cents / 100).toFixed(2)}` : ''}{' '}
                  on {selectedBooking.refunded_at ? formatDate(selectedBooking.refunded_at) : '—'}
                  {selectedBooking.refund_reason ? ` — ${selectedBooking.refund_reason}` : ''}
                </Alert>
              )}
            </Stack>
          </Box>
        )}
      </Drawer>

      {/* Assign Intern Dialog */}
      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Intern to Booking</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Select Intern</InputLabel>
            <Select
              value={selectedInternId}
              onChange={(e) => setSelectedInternId(e.target.value)}
              label="Select Intern"
            >
              {availableInterns.map((intern) => (
                <MenuItem key={intern.id || intern.intern_id || intern.email} value={intern.id || intern.intern_id}>
                  {intern.name || `${intern.first_name || ''} ${intern.last_name || ''}`} ({intern.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAssignOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleAssignIntern} variant="contained" disabled={!selectedInternId || actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirm */}
      <ConfirmDialog
        open={cancelOpen}
        title="Cancel Booking"
        message="Are you sure you want to cancel this booking? This action cannot be undone."
        confirmText="Cancel Booking"
        confirmColor="error"
        onConfirm={handleCancelBooking}
        onCancel={() => setCancelOpen(false)}
      />

      {/* Revert to Confirmed Dialog */}
      <Dialog open={revertOpen} onClose={() => { setRevertOpen(false); setRevertReason(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Revert Booking to Confirmed</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Use this when an intern clicked Start Assessment or Mark Complete by mistake and the booking needs to go back to Confirmed so they can retry. The intern will see it as Confirmed in their Upcoming tab again.
          </Alert>
          <TextField
            label="Reason (required)"
            value={revertReason}
            onChange={(e) => setRevertReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="e.g. Intern misclicked Mark Complete; assessment never happened. Contacted intern, rescheduling."
            required
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setRevertOpen(false); setRevertReason(''); }} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleRevertBooking}
            variant="contained"
            color="warning"
            disabled={!revertReason.trim() || actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Revert'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Issue Refund Dialog */}
      <Dialog
        open={refundOpen}
        onClose={() => { if (!actionLoading) closeRefundDialog(); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Issue Refund</DialogTitle>
        <DialogContent>
          {refundResult?.ok ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Refund issued. Refund ID: {refundResult.refund_id}
              {refundResult.amount_cents != null && ` for $${(refundResult.amount_cents / 100).toFixed(2)}`}.
              Stripe will return funds to the client's original payment method
              within 5-10 business days.
            </Alert>
          ) : (
            <>
              {refundResult?.error && (
                <Alert severity="error" sx={{ mb: 2 }}>{refundResult.error}</Alert>
              )}
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will issue a Stripe refund and mark the booking as refunded.
                Cannot be undone. Make sure you've already notified the client.
              </Alert>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Refund Amount</InputLabel>
                  <Select
                    value={refundFullAmount ? 'full' : 'partial'}
                    label="Refund Amount"
                    onChange={(e) => setRefundFullAmount(e.target.value === 'full')}
                  >
                    <MenuItem value="full">Full Refund</MenuItem>
                    <MenuItem value="partial">Partial Refund (specify amount)</MenuItem>
                  </Select>
                </FormControl>
                {!refundFullAmount && (
                  <TextField
                    label="Amount in dollars"
                    value={refundAmountDollars}
                    onChange={(e) => setRefundAmountDollars(e.target.value)}
                    type="number"
                    inputProps={{ min: '0.01', step: '0.01' }}
                    fullWidth
                    required
                    placeholder="e.g. 50.00"
                    autoFocus
                  />
                )}
                <TextField
                  label="Reason (required, recorded with refund)"
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="e.g. Client cancelled with 48h notice. Service not delivered."
                  required
                  autoFocus={refundFullAmount}
                />
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {refundResult?.ok ? (
            <Button onClick={closeRefundDialog} variant="contained">Close</Button>
          ) : (
            <>
              <Button onClick={closeRefundDialog} color="inherit" disabled={actionLoading}>
                Cancel
              </Button>
              <Button
                onClick={handleIssueRefund}
                variant="contained"
                color="error"
                disabled={!refundReason.trim() || actionLoading}
              >
                {actionLoading ? <CircularProgress size={20} /> : 'Issue Refund'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog
        open={rescheduleOpen}
        onClose={() => { if (!actionLoading) closeRescheduleDialog(); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reschedule Booking</DialogTitle>
        <DialogContent>
          {rescheduleResult?.ok ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Rescheduled. {rescheduleResult.intern_kept
                ? 'The assigned intern was available at the new time and stayed assigned.'
                : rescheduleResult.intern_unassigned
                  ? 'The previously-assigned intern was not available at the new time. Booking is back in the pool for other interns.'
                  : ''}
              {' '}Notifications sent.
            </Alert>
          ) : (
            <>
              {rescheduleResult?.error && (
                <Alert severity="error" sx={{ mb: 2 }}>{rescheduleResult.error}</Alert>
              )}
              <Alert severity="info" sx={{ mb: 2 }}>
                Move this booking to a new time. If the assigned intern is still available at the new time,
                they stay assigned. Otherwise, they're unassigned and the booking goes back to the job board.
                Both the client and the (current or previously-assigned) intern are notified by email.
              </Alert>
              <Stack spacing={2}>
                <TextField
                  label="New date & time"
                  type="datetime-local"
                  value={rescheduleDateTime}
                  onChange={(e) => setRescheduleDateTime(e.target.value)}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Reason (required, included in client + intern emails)"
                  value={rescheduleReason}
                  onChange={(e) => setRescheduleReason(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="e.g. Client requested a different day. Original time conflicts with new commitment."
                  required
                />
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {rescheduleResult?.ok ? (
            <Button onClick={closeRescheduleDialog} variant="contained">Close</Button>
          ) : (
            <>
              <Button onClick={closeRescheduleDialog} color="inherit" disabled={actionLoading}>
                Cancel
              </Button>
              <Button
                onClick={handleReschedule}
                variant="contained"
                color="primary"
                disabled={!rescheduleDateTime || !rescheduleReason.trim() || actionLoading}
              >
                {actionLoading ? <CircularProgress size={20} /> : 'Reschedule'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Booking History Dialog */}
      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Booking History</DialogTitle>
        <DialogContent>
          {historyLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {historyError && <Alert severity="error" sx={{ mb: 2 }}>{historyError}</Alert>}
          {!historyLoading && !historyError && historyEvents.length === 0 && (
            <Alert severity="info">No history events recorded for this booking yet.</Alert>
          )}
          {!historyLoading && historyEvents.length > 0 && (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {historyEvents.map((ev, i) => (
                <Paper key={`${ev.type}-${ev.ts}-${i}`} variant="outlined" sx={{ p: 2, borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
                  <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={600}>{ev.label}</Typography>
                    <Chip label={ev.type} size="small" variant="outlined" />
                    {ev.actor && <Chip label={`by ${ev.actor}`} size="small" />}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {formatDate(ev.ts)}
                  </Typography>
                  {ev.details && Object.keys(ev.details).length > 0 && (
                    <Box sx={{ mt: 1, pl: 1 }}>
                      {Object.entries(ev.details).map(([k, v]) => {
                        if (v == null || v === '') return null;
                        return (
                          <Typography key={k} variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                            <strong>{k}:</strong> {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                          </Typography>
                        );
                      })}
                    </Box>
                  )}
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setHistoryOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function DetailRow({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}
